import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import BrandButton from "components/BrandButton";
import CustomAlert from "components/CustomAlert";
import { FormikProvider, useFormik } from "formik";
import { observer } from "mobx-react";
import BottleLabel, { Customization } from "models/bottle-label";
import { FC, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useParams } from "react-router";
import { useMedia } from "react-use";
import {
  processLabels,
  processPreviews,
  setUploadFiles,
} from "scenes/Cask/scenes/Orders/scenes/Bottling/components/BottlingForm";
import LabelPreview from "scenes/Cask/scenes/Orders/scenes/Bottling/components/label-editor/LabelPreview";
import LabelsFormPart from "scenes/Cask/scenes/Orders/scenes/Bottling/components/LabelsFormPart";
import OrdersService, { EditLabelsVm } from "services/orders";
import { useStores } from "stores";
import { formatToString } from "utils/format";
import * as Yup from "yup";
import CaskSelector from "./components/CaskSelector";

export interface LabelEditorFormValues {
  caskNumber: string;
  showSummary: boolean;
  labels: {
    labels: BottleLabel[];
  };
}

const validationSchema = Yup.object().shape({
  labels: Yup.object().shape({
    labels: Yup.array().of(
      Yup.object().shape({
        optOut: Yup.boolean(),
        touched: Yup.boolean().when("optOut", {
          is: false,
          then: Yup.boolean().oneOf([true], "Obligatoriskt"),
        }),
      }),
    ),
  }),
});

const initialValues: LabelEditorFormValues = {
  caskNumber: "",
  showSummary: false,
  labels: {
    labels: [
      {
        customization: Customization.File,
        percentOfLabels: 100,
        imageFile: undefined,
        optOut: false,
        touched: false,
        text: "",
      },
    ],
  },
};

const LabelEditorPage: FC = () => {
  const { localeStore } = useStores();
  const isMobile = useMedia("(max-width: 48em)");
  const [completed, setCompleted] = useState(false);
  const { id } = useParams<{ id?: string }>();
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const labels = await processLabels(
        values.labels.labels,
        values.caskNumber,
      );
      const previews = await processPreviews(values.caskNumber);

      const payload: EditLabelsVm = {
        language: localeStore.language,
        caskId: parseInt(id!),
        bottleLabels: labels,
        bottleLabelPreviews: previews,
      };

      // Submit to API
      const orderResponse = await OrdersService.editLabels(payload);
      if (orderResponse.hasErrors) {
        throw new Error("Failed to submit label edit");
      } else {
        window.scrollTo(0, 0);
        setCompleted(true);
      }
    },
  });

  if (completed) {
    return (
      <Container maxW="container.sm" py={8}>
        <VStack spacing={8} align="stretch" textAlign="center">
          <Box>
            <Heading size="xl" mb={2}>
              <FormattedMessage
                id="labelEditor.completed.title"
                defaultMessage="Komplettering skickad"
              />
            </Heading>
            <Text fontSize="lg" color="gray.600">
              <FormattedMessage
                id="labelEditor.completed.message"
                defaultMessage="Tack för att du kompletterade dina etiketter. Vi återkommer om vi har några frågor."
              />
            </Text>
          </Box>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="container.lg" py={8}>
      <VStack spacing={8} align="stretch">
        <Box>
          <Heading size="xl" mb={2}>
            <FormattedMessage
              id="labelEditor.title"
              defaultMessage="Komplettera etiketter"
            />
          </Heading>
        </Box>

        <FormikProvider value={formik}>
          <Box as="form" onSubmit={formik.handleSubmit}>
            <VStack spacing={8} align="stretch">
              {/* Cask Selector */}
              {!formik.values.showSummary && <CaskSelector id={id || ""} />}

              {/* Labels Form Part - Only show if cask is selected */}
              {id && (
                <>
                  <Box display={!formik.values.showSummary ? "block" : "none"}>
                    <LabelsFormPart />
                  </Box>

                  <Box display={formik.values.showSummary ? "block" : "none"}>
                    <Box mt={4} mb={8}>
                      <CustomAlert
                        labelKey={
                          formik.values.labels.labels.length > 1
                            ? "cask.orders.bottling.summary.labels.warning.plural"
                            : "cask.orders.bottling.summary.labels.warning.title"
                        }
                        status="warning"
                      />
                    </Box>
                    <Wrap spacing={6} mt={2} w="full">
                      {formik.values.labels.labels.map((l, i) => (
                        <WrapItem key={`label-${i}`}>
                          <Box
                            w={isMobile ? "calc(100vw - 3rem)" : "auto"}
                            h="full"
                            display="flex"
                            flexDir="column"
                            justifyContent="space-between"
                          >
                            <LabelPreview label={l} index={i} summary />
                            {formik.values.labels.labels.length > 1 && (
                              <Box w="full" textAlign="center" mt={4}>
                                <Text
                                  mt={2}
                                  fontWeight={400}
                                  fontSize="lg"
                                >{`${formatToString(
                                  "cask.orders.bottling.summary.labels.percent",
                                )} ${l.percentOfLabels} %`}</Text>
                              </Box>
                            )}
                          </Box>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>

                  {/* Sticky Footer with Submit Button */}
                  <Box
                    position="sticky"
                    bottom={0}
                    bg="whiteAlpha.700"
                    p={4}
                    mt={8}
                    zIndex={10}
                  >
                    <Container maxW="container.lg">
                      {formik.values.showSummary && (
                        <Box display="flex">
                          <BrandButton
                            onClick={() => {
                              window.scrollTo(0, 0);
                              formik.setFieldValue("showSummary", false);
                            }}
                            size="lg"
                          >
                            <FormattedMessage
                              id="labelEditor.back"
                              defaultMessage="Tillbaka"
                            />
                          </BrandButton>
                          <BrandButton
                            ml={4}
                            isLoading={formik.isSubmitting}
                            disabled={!formik.isValid || formik.isSubmitting}
                            colorScheme="brand"
                            type="submit"
                            size="lg"
                          >
                            <FormattedMessage
                              id="labelEditor.submit"
                              defaultMessage="Skicka komplettering"
                            />
                          </BrandButton>
                        </Box>
                      )}
                      {!formik.values.showSummary && (
                        <Box display="flex">
                          <BrandButton
                            onClick={async () => {
                              window.scrollTo(0, 0);

                              formik.setFieldValue("showSummary", true);
                              await setUploadFiles(formik as any);
                            }}
                            size="lg"
                            colorScheme="brand"
                            isLoading={formik.isSubmitting}
                            disabled={!formik.isValid || formik.isSubmitting}
                          >
                            <FormattedMessage
                              id="labelEditor.continue"
                              defaultMessage="Fortsätt till granskning"
                            />
                          </BrandButton>
                        </Box>
                      )}
                    </Container>
                  </Box>
                </>
              )}
            </VStack>
          </Box>
        </FormikProvider>
      </VStack>
    </Container>
  );
};

export default observer(LabelEditorPage);
